/**
 * SatelliteRasterGLLayer.ts
 * المسار **السلس** لطبقة القمر الصناعي: نسيج GeoColor عالمي واحد (equirectangular RGB)
 * يولّده الـbackend (satellite_to_raster.py) بدمج كل الأقمار الجيوستاتيكية.
 *
 * لماذا هو موجود إلى جانب هرم البلاطات: البلاطات تعطي الحدّة عند التكبير، لكنها لا تعطي
 * حركةً سلسة على الشريط الزمني — كل إطار عشرات الطلبات، فالتشغيل السريع يتقطّع. هنا
 * نسيجان على الـGPU يُمزجان بـu_blend، فتتحرّك الغيوم بانسياب تامّ مهما تسارع الشريط،
 * وبطلب واحد لكل إطار. الطبقتان تعملان معاً: هذه في الأسفل دائماً (وهي أيضاً التغطية
 * التي تمنع أي ثقب أثناء تحميل البلاطات)، والبلاطات فوقها عند التقريب.
 *
 * قناع التغطية: صور الأقمار الجيوستاتيكية لا تصل القطبين، والنسيج JPEG بلا قناة ألفا
 * (أخفّ ~3× من PNG لسلسلة كاملة)، فالتلاشي القطبي يُحسب هنا في الشيدر من حدّي العرض
 * (lat_north/lat_south في الفهرس) — خارجهما تتلاشى الطبقة وتظهر قاعدة الخريطة بدل رقعة
 * سوداء.
 *
 * الصفّ 0 من الصورة = الشمال (lat +90)، يطابق مخرج السكربت (lats = 90 - ...).
 */

import { MercatorCoordinate, type CustomLayerInterface, type CustomRenderMethodInput, type Map as MaplibreMap } from 'maplibre-gl';
import { type GLContext, createProgram, getLocations, createTexture } from './glUtils';

/** عرض شريط التلاشي عند حدَّي التغطية القطبيّين (درجات) — قصّ حادّ يُظهر خطاً مستقيماً. */
const FADE_DEG = 5.0;

const VERTEX_SRC = `
attribute vec2 a_pos;
uniform mat4 u_matrix;
varying vec2 v_merc;
void main() { v_merc = a_pos; gl_Position = u_matrix * vec4(a_pos, 0.0, 1.0); }`;

const FRAGMENT_SRC = `
precision highp float;
varying vec2 v_merc;
uniform sampler2D u_tex;        // الإطار الحالي
uniform sampler2D u_texNext;    // الإطار التالي
uniform float u_blend;          // 0 = الحالي فقط، 1 = التالي فقط
uniform float u_opacity;
uniform float u_latNorth;       // أقصى عرض شماليّ فيه بيانات
uniform float u_latSouth;       // أقصى عرض جنوبيّ فيه بيانات
const float PI = 3.141592653589793;
const float FADE = ${FADE_DEG.toFixed(1)};

void main() {
    float u = fract(v_merc.x);                                   // الطول ملفوف عبر نسخ العالم
    float latRad = 2.0 * atan(exp(PI * (1.0 - 2.0 * v_merc.y))) - PI * 0.5;
    float lat = degrees(latRad);
    // equirect: الصفّ 0 = الشمال (+90) → t=0. t = (90 - lat)/180.
    float t = (90.0 - lat) / 180.0;
    if (t < 0.0 || t > 1.0) discard;

    // التغطية قطبيّة فقط: الصورة متّصلة أفقياً بين الحدّين (السكربت ملأ فجوات الأقمار)،
    // فيكفي تلاشٍ ناعم عند طرفيها بدل قصّ حادّ يرسم خطاً مستقيماً عبر الخريطة.
    float cover = smoothstep(u_latNorth, u_latNorth - FADE, lat)
                * smoothstep(u_latSouth, u_latSouth + FADE, lat);
    if (cover <= 0.001) discard;

    vec3 c = mix(texture2D(u_tex, vec2(u, t)).rgb, texture2D(u_texNext, vec2(u, t)).rgb, u_blend);
    // لا قصّ حسب السطوع: داخل الحدّين لا بكسل غير مغطّى، وقصُّ الداكن كان يثقب جانب
    // الليل (محيطات GeoColor ليلاً شبه سوداء) فتظهر رقع مفقودة وسط الصورة.
    float a = u_opacity * cover;
    gl_FragColor = vec4(c * a, a);
}`;

const LAT_LIMIT = 85.051129;

export class SatelliteRasterGLLayer implements CustomLayerInterface {
    public readonly id: string;
    public readonly type = 'custom' as const;
    public readonly renderingMode = '2d' as const;

    private map: MaplibreMap | null = null;
    private program: WebGLProgram | null = null;
    private loc: ReturnType<typeof getLocations> | null = null;
    private quadBuffer: WebGLBuffer | null = null;
    private tex: WebGLTexture | null = null;
    private texNext: WebGLTexture | null = null;
    private ready = false;
    private destroyed = false;

    private url = '';
    private urlNext = '';
    private blend = 0;
    private opacity: number;
    private latNorth = 81.0;
    private latSouth = -81.0;
    private glRef: GLContext | null = null;
    // رقم تسلسلي لكل فتحة نسيج: تحميل الصور غير متزامن، فبدونه قد تصل صورة قديمة بعد
    // صورة أحدث فتُكتب فوقها (الشريط الزمني يبدّل الإطارات أسرع من الشبكة).
    //
    // العدّاد **عالميّ** لا لكل فتحة: setFrames يبدّل الفتحتين، فعدّاد خاصّ بكل فتحة يعود
    // إلى الوراء عند التبديل، وقد تُنتج زياداتٌ لاحقة رقماً يطابق رقم طلبٍ ما زال طائراً
    // فتُقبل صورته القديمة على أنها الجديدة. الرموز العالمية لا تتكرّر أبداً فينتفي الاحتمال.
    private genCounter = 0;
    /** رمز آخر طلب أُطلق لكل فتحة (يُبدَّل مع النسيجين). */
    private slotGen: [number, number] = [0, 0];
    /** الروابط المحمَّلة فعلاً في كل فتحة — تُستخدم لإعادة استعمال نسيج بدل إعادة تحميله. */
    private loadedUrl: [string, string] = ['', ''];

    constructor(id: string, url: string, opacity = 1) {
        this.id = id;
        this.url = url;
        this.opacity = opacity;
    }

    /**
     * يضبط إطارَي المزج معاً.
     *
     * دالّة واحدة لا اثنتان: أثناء التشغيل يصير «التالي» هو «الحالي» في الخطوة التالية،
     * وضبطهما منفصلَين كان يعيد تحميل الصورة نفسها من الشبكة عند كل خطوة — أي وميض
     * وتقطيع عند كل إطار. هنا نكتشف الإزاحة وننقل النسيج بين الفتحتين بلا أي طلب.
     */
    setFrames(url: string, urlNext: string) {
        if (url === this.url && urlNext === this.urlNext) return;
        const shiftedForward = url !== '' && url === this.loadedUrl[1];
        this.url = url;
        this.urlNext = urlNext;
        if (!this.glRef) return;
        if (shiftedForward) {
            // الإطار التالي صار الحالي: نبدّل النسيجين بدل تحميل أيّهما من جديد.
            [this.tex, this.texNext] = [this.texNext, this.tex];
            [this.slotGen[0], this.slotGen[1]] = [this.slotGen[1], this.slotGen[0]];
            this.loadedUrl = [this.loadedUrl[1], ''];
            this.ready = true;
            if (urlNext) this.loadInto(1, urlNext);   // الفتحة 1 صارت فارغة بعد الإزاحة
            this.map?.triggerRepaint();
            return;
        }
        if (url && url !== this.loadedUrl[0]) this.loadInto(0, url);
        if (urlNext && urlNext !== this.loadedUrl[1]) this.loadInto(1, urlNext);
    }

    setBlend(blend: number) {
        const b = Math.max(0, Math.min(1, blend));
        if (b === this.blend) return;
        this.blend = b;
        this.map?.triggerRepaint();
    }
    setOpacity(o: number) {
        if (o === this.opacity) return;
        this.opacity = o;
        this.map?.triggerRepaint();
    }
    /** حدّا العرض اللذان تمتدّ بينهما بيانات الموزاييك (satellite_meta.json). */
    setCoverage(latNorth: number, latSouth: number) {
        if (latNorth === this.latNorth && latSouth === this.latSouth) return;
        this.latNorth = latNorth;
        this.latSouth = latSouth;
        this.map?.triggerRepaint();
    }
    /** هل النسيج الحالي محمَّل؟ تستخدمه الواجهة لتقرّر متى تُظهر الطبقة. */
    isReady() { return this.ready; }

    onAdd(map: MaplibreMap, gl: GLContext) {
        this.map = map;
        this.glRef = gl;
        this.program = createProgram(gl, VERTEX_SRC, FRAGMENT_SRC);
        this.loc = getLocations(gl, this.program, ['a_pos'],
            ['u_matrix', 'u_tex', 'u_texNext', 'u_blend', 'u_opacity', 'u_latNorth', 'u_latSouth']);
        const blank = new Uint8Array([0, 0, 0, 0]);
        this.tex = createTexture(gl, 1, 1, blank, { filter: gl.LINEAR });
        this.texNext = createTexture(gl, 1, 1, blank, { filter: gl.LINEAR });

        // رباعي يغطّي عدّة نسخ عالم أفقياً (كـ RasterHeatmapGLLayer) كي تظهر في كل النسخ.
        const yN = MercatorCoordinate.fromLngLat({ lng: 0, lat: LAT_LIMIT }).y;
        const yS = MercatorCoordinate.fromLngLat({ lng: 0, lat: -LAT_LIMIT }).y;
        const N = 3;
        const x0 = -N, x1 = 1 + N;
        this.quadBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([x0, yN, x0, yS, x1, yN, x1, yS]), gl.STATIC_DRAW);

        this.loadedUrl = ['', ''];
        if (this.url) this.loadInto(0, this.url);
        if (this.urlNext) this.loadInto(1, this.urlNext);
    }

    /**
     * يحمّل صورة في فتحة نسيج (0 = الحالي، 1 = التالي) دون لمس الأخرى.
     *
     * **الفكّ خارج الخيط الرئيسي (createImageBitmap).** النسيج العالمي 4096×2048 يساوي
     * ~33 ميغابايت بعد الفكّ، وكان المسار السابق (`new Image()` ثم `texImage2D(img)`)
     * يفرض على الخيط الرئيسي فكّ الـJPEG ونسخه إلى الـGPU دفعةً واحدة — نتوء عشرات
     * المللي عند **كل** خطوة إطار، أي إطارات مفقودة في اللحظة التي يُفترض أن يكون
     * الانتقال فيها أنعم ما يكون. `createImageBitmap` يفكّ في خيط المتصفّح الداخلي
     * ويسلّم كائناً جاهزاً للرفع بلا تحويل إضافي؛ والنتيجة بكسل-ببكسل مطابقة: لا ضغط
     * ولا تصغير ولا أي مساس بالدقّة.
     *
     * ولا يمرّ عبر `rasterImageCache` عمداً: ذلك الكاش يحدّ بالعدد (أربعون) لا
     * بالبايتات، وأربعون نسيجاً عالمياً ≈ 1.3 غيغابايت. الفتحتان هنا هما كل ما يلزم،
     * وبايتات الإطارات المجاورة محفوظة في كاش الشبكة أصلاً (الإحماء في
     * SatelliteTileLayer ينزّل بايتات فقط لهذا السبب بالذات).
     */
    private loadInto(slot: 0 | 1, url: string) {
        const gl = this.glRef;
        if (!gl || !url) return;
        const token = ++this.genCounter;
        this.slotGen[slot] = token;

        const upload = (img: ImageBitmap | HTMLImageElement) => {
            const bitmap = img as ImageBitmap & { close?: () => void };
            // الفكّ غير متزامن: قد تكون الفتحة أُعيد إطلاقها أو الطبقة أُزيلت أثناءه.
            if (this.destroyed || this.slotGen[slot] !== token) { bitmap.close?.(); return; }
            // النسيج قد يكون تبادل موضعه بعد إطلاق الطلب (setFrames)، فنعيد قراءته.
            const target = slot === 0 ? this.tex : this.texNext;
            if (!target) { bitmap.close?.(); return; }
            try {
                gl.bindTexture(gl.TEXTURE_2D, target);
                gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
                this.loadedUrl[slot] = url;
                if (slot === 0) this.ready = true;
                this.map?.triggerRepaint();
            } catch { /* تجاهل */ }
            // إغلاق آمن هنا (بخلاف rasterImageCache): الـbitmap مملوك حصرياً لهذه
            // الاستدعاءة ولا مرجع آخر إليه بعد الرفع إلى الـGPU.
            finally { bitmap.close?.(); }
        };

        /** المسار الاحتياطي: متصفّح بلا createImageBitmap، أو فشل الجلب/الفكّ. */
        const viaImage = () => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => upload(img);
            img.src = url;
        };

        if (typeof createImageBitmap !== 'function') { viaImage(); return; }
        fetch(url, { mode: 'cors', credentials: 'omit', cache: 'default' })
            .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
            .then((blob) => createImageBitmap(blob))
            .then(upload)
            .catch(viaImage);
    }

    onRemove(_map: MaplibreMap, gl: GLContext) {
        this.destroyed = true;
        if (this.program) gl.deleteProgram(this.program);
        if (this.quadBuffer) gl.deleteBuffer(this.quadBuffer);
        if (this.tex) gl.deleteTexture(this.tex);
        if (this.texNext) gl.deleteTexture(this.texNext);
        this.program = this.quadBuffer = this.tex = this.texNext = null;
        this.map = this.glRef = null;
    }

    private errorLogged = false;
    render(gl: GLContext, options: CustomRenderMethodInput) {
        if (!this.program || !this.loc || !this.ready || !this.quadBuffer || !this.tex || !this.texNext) return;
        if (this.opacity <= 0.001) return;
        try {
            gl.useProgram(this.program);
            gl.uniformMatrix4fv(this.loc.uniforms.u_matrix, false, new Float32Array(options.defaultProjectionData.mainMatrix as ArrayLike<number>));
            gl.uniform1f(this.loc.uniforms.u_opacity, this.opacity);
            // بلا إطار تالٍ محمَّل يبقى المزج صفراً — وإلا ظهر النسيج الفارغ لحظةً.
            gl.uniform1f(this.loc.uniforms.u_blend, this.loadedUrl[1] ? this.blend : 0);
            gl.uniform1f(this.loc.uniforms.u_latNorth, this.latNorth);
            gl.uniform1f(this.loc.uniforms.u_latSouth, this.latSouth);
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, this.tex);
            gl.uniform1i(this.loc.uniforms.u_tex, 0);
            gl.activeTexture(gl.TEXTURE1);
            gl.bindTexture(gl.TEXTURE_2D, this.texNext);
            gl.uniform1i(this.loc.uniforms.u_texNext, 1);
            gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
            gl.enableVertexAttribArray(this.loc.attributes.a_pos);
            gl.vertexAttribPointer(this.loc.attributes.a_pos, 2, gl.FLOAT, false, 0, 0);
            gl.disable(gl.CULL_FACE);
            gl.disable(gl.DEPTH_TEST);
            gl.enable(gl.BLEND);
            // ألفا مضروبة مسبقاً في الـshader (gl_FragColor = vec4(c*a, a)) — يمنع هالة
            // سوداء على حافة التلاشي التي يُنتجها المزج غير المضروب مسبقاً.
            gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        } catch (e) {
            if (!this.errorLogged) { console.error(`SatelliteRasterGLLayer(${this.id})`, e); this.errorLogged = true; }
        }
    }
}
